import type { TipId } from '@/tips';
import { formatPercent } from '@/utils/format';

export interface DashboardMetric {
  key: string;
  nameKey: string;
  value: number | null;
  money?: { amountMinor: number; currency: string };
  comparison?: {
    change: number;
    previousValue: number | null;
    previousMoney?: { amountMinor: number; currency: string };
  };
  updatedAt: string;
}

export interface ChartPoint {
  key: string;
  label: string;
  value: number;
  currency?: string;
  grain?: 'day' | 'week' | 'month';
}

export interface BirthdayPerson {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

export interface DashboardPersonPreview {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  joinedAt: string | null;
}

export interface DashboardFollowUpPreview {
  id: string;
  memberId: string;
  firstName: string;
  lastName: string;
  reason: string;
  dueDate: string | null;
}

export interface DashboardJoinRequestPreview {
  id: string;
  firstName: string;
  lastName: string;
}

export interface DashboardContentPreview {
  id: string;
  title: string;
  occurredOn: string | null;
  subtitle: string | null;
}

export interface DashboardCalendarEvent {
  id: string;
  title: string;
  startsAt: string | null;
}

export interface DashboardCalendarDay {
  date: string;
  count: number;
  events: DashboardCalendarEvent[];
}

export interface DashboardResponse {
  range: {
    dateFrom: string;
    dateTo: string;
    previousFrom: string;
    previousTo: string;
  };
  updatedAt: string;
  metrics: DashboardMetric[];
  charts: {
    givingTrend: ChartPoint[];
    givingByFund: ChartPoint[];
    membersByGender: ChartPoint[];
    membersByType: ChartPoint[];
    membersByGroup: ChartPoint[];
    membersByAutoFilledGroup: ChartPoint[];
    membersByManualGroup: ChartPoint[];
  };
  birthdays: {
    today: BirthdayPerson[];
    thisMonth: BirthdayPerson[];
  };
  previews: {
    newMembers: DashboardPersonPreview[];
    firstTimers: DashboardPersonPreview[];
    openFollowUps: DashboardFollowUpPreview[];
    pendingMemberRequests: DashboardJoinRequestPreview[];
  };
  calendar: {
    month: string;
    fromDate: string;
    toDate: string;
    days: DashboardCalendarDay[];
  };
  content: {
    sermons: DashboardContentPreview[];
    announcements: DashboardContentPreview[];
    devotionals: DashboardContentPreview[];
  };
}

export const METRIC_TIP_IDS: Partial<Record<string, TipId>> = {
  active_members: 'dashboard.members.active',
  giving_in_period: 'dashboard.giving.month',
  attendance_recent: 'dashboard.attendance.recent',
  messages_delivered_in_period: 'dashboard.messages.delivered',
  campaigns_sent_in_period: 'dashboard.messages.campaigns',
  delivery_failures_in_period: 'dashboard.messages.failures',
};

export const formatDashboardChange = (change: number): string => {
  const formatted = formatPercent(Math.abs(change));
  if (change > 0) return `+${formatted}`;
  if (change < 0) return `-${formatted}`;
  return formatted;
};

/** Drill-down routes for dashboard metric and chart widgets. */
export const DASHBOARD_WIDGET_ROUTES: Partial<Record<string, string>> = {
  giving_in_period: '/giving',
  gifts_in_period: '/giving',
  online_gifts_in_period: '/giving',
  active_members: '/people',
  new_members_in_period: '/people',
  first_timers_in_period: '/people',
  open_follow_ups: '/people/follow-ups',
  pending_member_requests: '/people/join-requests',
  birthdays_today_count: '/people/birthdays',
  messages_delivered_in_period: '/messaging',
  campaigns_sent_in_period: '/messaging',
  delivery_failures_in_period: '/messaging',
  check_ins_in_period: '/attendance',
  unique_attendees_in_period: '/attendance',
  expenses_paid_in_period: '/expenses',
  expenses_paid_count_in_period: '/expenses',
  expenses_pending_approval: '/expenses',
  active_groups: '/groups',
  group_memberships: '/groups',
  pending_group_requests: '/groups',
  chart_giving_trend: '/giving',
  chart_giving_by_fund: '/giving/funds',
  chart_members_by_gender: '/people',
  chart_members_by_type: '/people',
  chart_members_by_group: '/groups',
  birthdays: '/people/birthdays',
  calendar: '/calendar',
  recent_sermons: '/content/sermons',
  recent_announcements: '/content/announcements',
  recent_devotionals: '/content/devotionals',
};

